<!-- Page header -->
<div class="hh-page-header">
  <button pButton type="button" class="p-button-text p-button-sm" (click)="goBack()">
    <i class="pi pi-arrow-left"></i>
  </button>
  <span class="hh-page-title">Bin {{ addonBin?.id }} - {{ addonBin?.account?.accountName }}</span>
</div>

<div *ngIf="isPageReady" class="flex flex-col h-full">
  <!-- Station info -->
  <div class="p-3 flex flex-col gap-2 border-b" style="border-color: var(--p-surface-border)">
    <div class="flex justify-between items-center">
      <div *ngIf="addonBin.pendingAddonStation">
        <div class="hh-card-label">Pending Station</div>
        <div class="text-base font-semibold">{{ addonBin.pendingAddonStation.name }}</div>
      </div>
      <div *ngIf="addonBin.currentAddonStation">
        <div class="hh-card-label">Current Station</div>
        <div class="text-base font-semibold">{{ addonBin.currentAddonStation.name }}</div>
      </div>
      <div *ngIf="addonBin.currentAddonBinStationStatus">
        <div class="hh-card-label">Status</div>
        <div class="text-base font-semibold">{{ addonBin.currentAddonBinStationStatus.name }}</div>
      </div>
    </div>
  </div>

  <!-- Scrollable content area -->
  <div class="flex-1 overflow-y-auto p-3 flex flex-col gap-3">

    <!-- Scanner (when pending station exists) -->
    <div *ngIf="canScanIntoStation()" class="hh-scanner">
      <uni-scanner-input
        [label]="'Scan into \'' + addonBin.pendingAddonStation?.name + '\'...'"
        busyLabel="Processing..."
        [searchFn]="searchItemDetailForScan"
        (scanResult)="onScanSuccess($event)"
        (scanError)="onScanError($event)">
      </uni-scanner-input>
    </div>

    <!-- Progress bar -->
    <div *ngIf="canScanIntoStation()" class="flex flex-col gap-1">
      <div class="hh-progress-bar">
        <div class="hh-progress-fill" [style.width.%]="progressPercent"></div>
      </div>
      <div class="text-sm text-center font-semibold">
        {{ scannedCount }} of {{ totalCount }} items scanned
      </div>
    </div>

    <!-- Last scanned feedback -->
    <div *ngIf="lastScannedItem" class="hh-card" style="border-color: var(--p-green-300); background-color: var(--p-green-50)">
      <div class="hh-card-label" style="color: var(--p-green-700)">Last scanned</div>
      <div class="font-semibold">{{ lastScannedItem.itemDetailId }}</div>
      <div class="text-sm opacity-70">
        {{ lastScannedItem.itemDetail?.product?.productType?.style }}
        - {{ lastScannedItem.itemDetail?.product?.productType?.description }}
      </div>
    </div>

    <!-- Bin complete message -->
    <div *ngIf="isBinComplete()" class="hh-card text-center py-6" style="border-color: var(--p-green-300); background-color: var(--p-green-50)">
      <i class="pi pi-check-circle text-4xl mb-2" style="color: var(--p-green-500)"></i>
      <div class="text-lg font-semibold" style="color: var(--p-green-700)">Bin Complete</div>
      <div class="text-sm opacity-70">All stations have been processed</div>
    </div>

    <!-- Scan All button (admin only) -->
    <button *ngIf="canScanAll()" class="hh-btn hh-btn-secondary" (click)="scanAll()">
      Scan All Items
    </button>

    <!-- Toggle item list -->
    <button class="hh-btn hh-btn-secondary" (click)="showItemList = !showItemList">
      <i class="pi mr-2" [ngClass]="showItemList ? 'pi-eye-slash' : 'pi-eye'"></i>
      {{ showItemList ? 'Hide Items' : 'Show Items' }} ({{ itemDetailAddons.length }})
    </button>

    <!-- Item list view -->
    <div *ngIf="showItemList" class="flex flex-col gap-2">
      <div *ngFor="let itda of itemDetailAddons" class="hh-card flex flex-col gap-1"
           [style.border-left]="isItemScanned(itda) ? '3px solid var(--p-green-500)' : '3px solid var(--p-surface-300)'">
        <div class="flex justify-between items-center">
          <span class="font-semibold text-sm">{{ itda.itemDetailId }}</span>
          <span class="hh-badge" [ngClass]="isItemScanned(itda) ? 'hh-badge-scanned' : 'hh-badge-pending'">
            {{ isItemScanned(itda) ? 'Scanned' : 'Pending' }}
          </span>
        </div>
        <div class="text-sm opacity-70">
          {{ itda.itemDetail?.product?.productType?.style }}
          - {{ itda.itemDetail?.product?.productType?.description }}
        </div>
        <div class="text-xs opacity-50">
          {{ itda.addon?.nameAndLocation }} &middot; {{ itda.addon?.addonStation?.name }}
        </div>
        <button *ngIf="canManuallyMarkScanned(itda)"
                class="hh-btn hh-btn-secondary mt-1"
                style="min-height: 36px; font-size: 0.8125rem"
                (click)="markAsScanned(itda)">
          Mark Scanned
        </button>
      </div>
    </div>
  </div>

  <!-- Station action buttons (sticky bottom) -->
  <div class="p-3 flex flex-col gap-2 border-t shrink-0" style="border-color: var(--p-surface-border); background-color: var(--p-surface-card)">
    <div class="flex gap-2">
      <button *ngIf="canMarkStationInProcess()"
              class="hh-btn hh-btn-primary flex-1"
              (click)="markStation(AddonBinStationStatusEnum.InProcess)">
        <i class="pi pi-play mr-2"></i> In Process
      </button>
      <button *ngIf="canMarkStationComplete()"
              class="hh-btn hh-btn-success flex-1"
              (click)="markStation(AddonBinStationStatusEnum.Closed)">
        <i class="pi pi-check mr-2"></i> Complete
      </button>
      <button *ngIf="canMarkStationOnHold()"
              class="hh-btn hh-btn-warning flex-1"
              (click)="markStation(AddonBinStationStatusEnum.OnHold)">
        <i class="pi pi-pause mr-2"></i> On Hold
      </button>
    </div>
    <button *ngIf="isBinComplete()" class="hh-btn hh-btn-secondary" (click)="goBack()">
      <i class="pi pi-arrow-left mr-2"></i> Back to Bins
    </button>
  </div>
</div>
